// Упрощение полилинии (алгоритм Дугласа-Пекера) - используется при загрузке
// уже сохранённого маршрута/GPX-импорта в редактор, чтобы получить разумное
// число перетаскиваемых вершин вместо сотен точек детальной геометрии (шаг
// ~30м на бэкенде, см. geometry.py::_resample_points). Возвращает ИНДЕКСЫ
// сохранённых точек (не сами координаты), чтобы вызывающая сторона могла
// нарезать исходный массив на "леги" между соседними вершинами, сохранив
// оригинальную детальную геометрию каждого отрезка - см.
// composables/useRouteEditor.ts::loadRoute/loadFromGpxImport.
export type Coordinate = [number, number];

const EARTH_RADIUS_M = 6371000;
const DEG_TO_RAD = Math.PI / 180;

// Локальная плоская проекция (метры) вокруг средней широты отрезка -
// достаточно точно для маршрутов туристического масштаба (десятки км),
// проще и дешевле полной sphere-aware cross-track формулы.
const toLocalXY = (c: Coordinate, refLatRad: number): [number, number] => {
  const metersPerDegLat = (EARTH_RADIUS_M * DEG_TO_RAD);
  const metersPerDegLng = metersPerDegLat * Math.cos(refLatRad);
  return [c[0] * metersPerDegLng, c[1] * metersPerDegLat];
};

const perpendicularDistanceM = (point: Coordinate, lineStart: Coordinate, lineEnd: Coordinate): number => {
  const refLatRad = ((lineStart[1] + lineEnd[1]) / 2) * DEG_TO_RAD;
  const [px, py] = toLocalXY(point, refLatRad);
  const [ax, ay] = toLocalXY(lineStart, refLatRad);
  const [bx, by] = toLocalXY(lineEnd, refLatRad);
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  return Math.hypot(px - projX, py - projY);
};

// Возвращает индексы точек, которые нужно сохранить как вершины (всегда
// включает первую и последнюю). toleranceM - максимальное допустимое
// перпендикулярное отклонение отброшенной точки от прямой между соседними
// сохранёнными вершинами.
export const simplifyLineIndices = (coords: Coordinate[], toleranceM: number): number[] => {
  if (coords.length < 3) return coords.map((_, i) => i);
  const keep = new Array(coords.length).fill(false);
  keep[0] = true;
  keep[coords.length - 1] = true;
  const stack: [number, number][] = [[0, coords.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop()!;
    let maxDist = -1;
    let maxIndex = -1;
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistanceM(coords[i], coords[start], coords[end]);
      if (d > maxDist) {
        maxDist = d;
        maxIndex = i;
      }
    }
    if (maxDist > toleranceM && maxIndex !== -1) {
      keep[maxIndex] = true;
      stack.push([start, maxIndex]);
      stack.push([maxIndex, end]);
    }
  }
  const result: number[] = [];
  for (let i = 0; i < coords.length; i++) if (keep[i]) result.push(i);
  return result;
};

// Если упрощение всё равно даёт слишком много вершин (очень длинный/петляющий
// трек), вершин для перетаскивания было бы неудобно много - постепенно
// увеличиваем допуск, пока не впишемся в maxVertices (но не выше
// maxToleranceM, чтобы не потерять форму совсем).
export const simplifyLineIndicesCapped = (
  coords: Coordinate[],
  { toleranceM = 12, maxVertices = 150, maxToleranceM = 80 }: { toleranceM?: number; maxVertices?: number; maxToleranceM?: number } = {}
): number[] => {
  let tolerance = toleranceM;
  let indices = simplifyLineIndices(coords, tolerance);
  while (indices.length > maxVertices && tolerance < maxToleranceM) {
    tolerance *= 1.6;
    indices = simplifyLineIndices(coords, tolerance);
  }
  return indices;
};

// Те же вершины, что выбирает simplifyLineIndices, но сразу координатами
// (для экспорта GPX и для payload превью-аналитики, где индексы исходного
// массива не нужны). Первая и последняя точки сохраняются всегда.
export const simplifyCoordinates = (coords: Coordinate[], toleranceM: number): Coordinate[] =>
  simplifyLineIndices(coords, toleranceM).map((i) => coords[i]);

// Не больше maxPoints вершин (лимит сервера на сохранение - 5000): короткая линия
// не меняется; длинную упрощаем с растущим допуском (4 -> 64 м), а если и этого
// мало - равномерно прореживаем. Первая и последняя точки сохраняются всегда.
export const simplifyToMaxPoints = (coords: Coordinate[], maxPoints: number): Coordinate[] => {
  if (coords.length <= maxPoints) return coords;
  const indices = simplifyLineIndicesCapped(coords, { toleranceM: 4, maxVertices: maxPoints, maxToleranceM: 64 });
  if (indices.length <= maxPoints) return indices.map((i) => coords[i]);
  const out: Coordinate[] = [];
  const step = (indices.length - 1) / (maxPoints - 1);
  for (let k = 0; k < maxPoints - 1; k++) out.push(coords[indices[Math.floor(k * step)]]);
  out.push(coords[indices[indices.length - 1]]);
  return out;
};
